/**
 * Aklasu Wishlist — V3
 * ---------------------
 * Stores product handles in localStorage (key: user_wishlist).
 * Backward-compatible with v2.7 data.
 *
 * Features:
 *   - Toggle save/unsave on product pages (.ak-wishlist-btn)
 *   - Visual filled-bookmark state for saved items
 *   - Wishlist page: fetches only saved products via Storefront
 *     /products/{handle}.js  (no looping through all products)
 *   - Header badge with saved-item count
 */
(function () {
  'use strict';

  /* ── helpers ──────────────────────────────────────────── */

  var STORAGE_KEY = 'user_wishlist';

  function getWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isInWishlist(handle) {
    return getWishlist().indexOf(handle) > -1;
  }

  function addToWishlist(handle) {
    var list = getWishlist();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      saveWishlist(list);
    }
    return list;
  }

  function removeFromWishlist(handle) {
    var list = getWishlist();
    var idx = list.indexOf(handle);
    if (idx > -1) {
      list.splice(idx, 1);
      saveWishlist(list);
    }
    return list;
  }

  /* ── button state ────────────────────────────────────── */

  function setButtonState(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
  }

  /* ── init buttons (product pages + collection tiles) ── */

  function initButtons() {
    var buttons = document.querySelectorAll('.ak-wishlist-btn');
    buttons.forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;

      /* Set initial state */
      if (isInWishlist(handle)) {
        setButtonState(btn, true);
      }

      /* Click handler */
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var active = btn.classList.contains('is-active');
        if (active) {
          removeFromWishlist(handle);
          setButtonState(btn, false);
        } else {
          addToWishlist(handle);
          setButtonState(btn, true);
        }
        updateHeaderCount();
      });
    });
  }

  /* ── header wishlist count badge ─────────────────────── */

  function updateHeaderCount() {
    var count = getWishlist().length;
    var badges = document.querySelectorAll('.ak-wishlist-count');
    badges.forEach(function (el) {
      el.textContent = count;
      // Explicit 'inline-flex' (not '') so the badge's shared
      // rule with .cart-count-bubble wins over the
      // .ak-wishlist-count default of display:none. Matches the
      // shared rule's display value so dimensions are stable.
      el.style.display = count > 0 ? 'inline-flex' : 'none';
    });
  }

  /* ── wishlist page ───────────────────────────────────── */

  function initWishlistPage() {
    var grid = document.querySelector('.ak-wishlist-grid');
    if (!grid) return;

    var emptyState = document.querySelector('.ak-wishlist-empty');
    var loader = document.querySelector('.ak-wishlist-loader');
    var list = getWishlist();

    if (list.length === 0) {
      if (loader) loader.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    /* Fetch each wishlisted product via /products/{handle}.js */
    var fetches = list.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (res) {
          if (!res.ok) throw new Error(handle + ' not found');
          return res.json();
        })
        .catch(function () {
          /* Product may have been deleted — silently remove from wishlist */
          removeFromWishlist(handle);
          return null;
        });
    });

    Promise.all(fetches).then(function (products) {
      /* Filter out any nulls (deleted products) */
      products = products.filter(Boolean);

      if (loader) loader.style.display = 'none';

      if (products.length === 0) {
        if (emptyState) emptyState.style.display = 'block';
        return;
      }

      products.forEach(function (product) {
        grid.appendChild(createProductCard(product));
      });

      grid.style.display = 'grid';
    });
  }

  /* ── product card builder for wishlist page ──────────── */

  function createProductCard(product) {
    var card = document.createElement('div');
    card.className = 'ak-wishlist-card';
    card.setAttribute('data-product-handle', product.handle);

    var img = product.featured_image
      ? product.featured_image
      : product.images && product.images[0]
        ? product.images[0]
        : '';

    /* Format price — Shopify .js returns price in cents */
    var price = product.price
      ? '$' + (product.price / 100).toFixed(2)
      : '';

    card.innerHTML =
      '<a class="ak-wishlist-card__link" href="/products/' + product.handle + '">' +
        '<div class="ak-wishlist-card__image-wrap">' +
          (img ? '<img class="ak-wishlist-card__image" src="' + img + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="400" height="400">' : '') +
        '</div>' +
        '<div class="ak-wishlist-card__info">' +
          '<h3 class="ak-wishlist-card__title">' + escapeHtml(product.title) + '</h3>' +
          '<p class="ak-wishlist-card__price">' + price + '</p>' +
        '</div>' +
      '</a>' +
      '<button class="ak-wishlist-card__remove ak-wishlist-btn is-active" data-product-handle="' + product.handle + '" aria-label="Remove from wishlist" type="button">' +
        '<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M5 2h10a1 1 0 0 1 1 1v14.5l-6-4-6 4V3a1 1 0 0 1 1-1z"/></svg>' +
      '</button>';

    /* Wire up the remove button */
    var removeBtn = card.querySelector('.ak-wishlist-card__remove');
    removeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      removeFromWishlist(product.handle);
      card.remove();
      updateHeaderCount();
      /* Show empty state if no cards left */
      var grid = document.querySelector('.ak-wishlist-grid');
      if (grid && grid.children.length === 0) {
        var empty = document.querySelector('.ak-wishlist-empty');
        if (empty) empty.style.display = 'block';
      }
    });

    return card;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── boot ─────────────────────────────────────────────── */

  function init() {
    initButtons();
    updateHeaderCount();
    initWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
